import { User, useAuth0 } from "@auth0/auth0-react";
import { useMemo } from "react";
import { authMockSub, isAuthEnabled } from "../utils/environment";
import { isDevelopIdeaflowIdEnabled, startDevelopSignIn, useDevelopSession } from "./DevelopSession";

export type IdeaflowUser = {
  /** Unique Ideaflow ID */
  id: string;
  /** ID received from auth0 */
  sub: string;
  first_name: string;
  last_name: string;
  email: string;
  isIdeaflowId?: boolean;
};

type Auth0Interface = Pick<
  ReturnType<typeof useAuth0>,
  | "error"
  | "isAuthenticated"
  | "isLoading"
  | "getAccessTokenSilently"
  | "getAccessTokenWithPopup"
  | "loginWithRedirect"
  | "loginWithPopup"
  | "logout"
> & {
  /** Ideaflow user */
  user?: User & IdeaflowUser;
};

const mockAuth = {
  // Auth state:
  error: undefined,
  isAuthenticated: true,
  isLoading: false,
  user: {
    id: authMockSub,
    sub: authMockSub,
    first_name: "Tyler",
    last_name: "Durden",
    nickname: "tyler",
    name: "Tyler Durden",
    picture: "/profile-default.jpg",
    locale: "en",
    updated_at: "2020-03-24T17:49:22.464Z",
    email: "bat@ideapad.io",
    email_verified: true,
  },
  // Auth methods:
  getAccessTokenSilently: () => Promise.resolve("INVALID_JWT_MOCK_AUTH" as any),
  getAccessTokenWithPopup: () => Promise.resolve("INVALID_JWT"),
  loginWithRedirect: () => Promise.resolve(),
  loginWithPopup: () => Promise.resolve(),
  logout: () => {},
};

const useMockAuth = (): Auth0Interface => mockAuth;

export const useAuth: () => Auth0Interface = !isAuthEnabled
  ? useMockAuth
  : () => {
      const auth = useAuth0();
      const develop = useDevelopSession();
      const user = auth.user;
      const loginWithRedirect: Auth0Interface["loginWithRedirect"] = async (...args: any[]) => {
        if (isDevelopIdeaflowIdEnabled()) {
          await startDevelopSignIn();
          return;
        }
        return auth.loginWithRedirect(...args);
      };
      const loginWithPopup: Auth0Interface["loginWithPopup"] = async (...args: any[]) => {
        if (isDevelopIdeaflowIdEnabled()) {
          await startDevelopSignIn();
          return;
        }
        return auth.loginWithPopup(...args);
      };

      const ideaflowUser = useMemo(() => {
        return user?.sub
          ? {
              ...user,
              id: user.sub,
              sub: user.sub,
              email: user.email ?? "unknown",
              first_name: user.first_name ?? user.given_name ?? "unknown",
              last_name: user.last_name ?? user.family_name ?? "unknown",
            }
          : undefined;
      }, [user]);

      if (develop.loading) return { ...auth, isLoading: true, isAuthenticated: false, user: undefined };
      if (develop.session) {
        const session = develop.session;
        const accessToken: Auth0Interface["getAccessTokenSilently"] = (options?: any): Promise<any> => {
          if (session.expiresAt <= Date.now())
            return Promise.reject(new Error("Develop session expired; sign in again"));
          return Promise.resolve(
            options?.detailedResponse
              ? {
                  access_token: session.token,
                  expires_in: Math.max(0, Math.floor((session.expiresAt - Date.now()) / 1000)),
                  scope: "",
                }
              : session.token,
          );
        };
        return {
          ...auth,
          loginWithRedirect,
          loginWithPopup,
          isLoading: false,
          isAuthenticated: true,
          error: undefined,
          user: {
            id: session.userId,
            sub: session.userId,
            first_name: "",
            last_name: "",
            email: "",
            isIdeaflowId: true,
          },
          getAccessTokenSilently: accessToken,
          getAccessTokenWithPopup: accessToken,
          logout: develop.logout,
        };
      }
      if (isDevelopIdeaflowIdEnabled())
        return {
          ...auth,
          loginWithRedirect,
          loginWithPopup,
          isLoading: false,
          isAuthenticated: false,
          user: undefined,
        };
      return {
        ...auth,
        loginWithRedirect,
        loginWithPopup,
        user: ideaflowUser,
      };
    };
